import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, UserCheck, Shield, Sparkles, ArrowRight, Home } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export default function EmailVerificationSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [userName, setUserName] = useState('');

  useEffect(() => {
    // Extract user name from URL params or use authenticated user data
    const nameParam = searchParams?.get('name');
    if (nameParam) {
      setUserName(nameParam);
    } else if (user?.user_metadata?.full_name) {
      setUserName(user?.user_metadata?.full_name);
    } else if (user?.email) {
      // Use email prefix as fallback
      setUserName(user?.email?.split('@')?.[0]);
    }
  }, [searchParams, user]);

  const handleCompleteSetup = () => {
    if (user) {
      // User is already authenticated, go directly to dashboard
      navigate('/user-dashboard');
    } else {
      // Not authenticated yet, redirect to login
      navigate('/user-login', { state: { verified: true } });
    }
  };

  const handleStartPlanning = () => {
    navigate('/ai-trip-planning-interface');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center px-4 py-12">
      <div className="max-w-2xl w-full">
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="absolute inset-0 bg-green-400 rounded-full blur-xl opacity-50 animate-pulse"></div>
            <div className="relative bg-green-500 rounded-full p-6">
              <CheckCircle className="w-16 h-16 text-white" strokeWidth={2.5} />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-green-100">
          {/* Success Message */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Email Verified Successfully!
            </h1>
            {userName && (
              <p className="text-xl text-gray-700 mb-4">
                Welcome aboard, <span className="font-semibold text-green-600">{userName}</span>! 🎉
              </p>
            )}
            <p className="text-gray-600 text-lg">
              Your email has been confirmed and your account is now active. You're all set to start planning amazing adventures!
            </p>
          </div>

          {/* Account Benefits */}
          <div className="bg-green-50 rounded-xl p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-green-600" />
              Your Account Benefits
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Save & Access Your Trips</p>
                  <p className="text-sm text-gray-600">Store all your trip plans in one secure place</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Trip History & Analytics</p>
                  <p className="text-sm text-gray-600">Track your adventures and get insights</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Personalized Recommendations</p>
                  <p className="text-sm text-gray-600">Get AI-powered suggestions tailored to you</p>
                </div>
              </div>
            </div>
          </div>

          {/* Onboarding Preview */}
          <div className="border-t border-gray-200 pt-8 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">What You Can Do Next</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">AI Trip Planning</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Let our AI help you create the perfect state park adventure
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Home className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-gray-900">Browse Adventures</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Explore curated trips from our community of adventurers
                </p>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="bg-gray-50 rounded-lg p-4 mb-8">
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <Shield className="w-5 h-5 text-gray-400" />
              <p>
                <span className="font-medium text-gray-900">Your privacy is protected.</span> We use industry-standard encryption and never share your data with third parties.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleCompleteSetup}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-green-600/30 hover:shadow-xl hover:shadow-green-600/40"
            >
              <UserCheck className="w-5 h-5" />
              {user ? 'Go to Dashboard' : 'Complete Setup & Login'}
              <ArrowRight className="w-5 h-5" />
            </button>

            <div className="grid md:grid-cols-2 gap-3">
              <button
                onClick={handleStartPlanning}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Start Planning a Trip
              </button>
              <button
                onClick={handleGoHome}
                className="w-full bg-white hover:bg-gray-50 text-gray-700 font-medium py-3 px-4 rounded-lg border-2 border-gray-300 hover:border-gray-400 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Home className="w-4 h-4" />
                Back to Home
              </button>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-6 text-sm text-gray-500">
          <p>Need help? Contact us at support@parktripplanner.com</p>
        </div>
      </div>
    </div>
  );
}